import axios from "axios";
import { Upload } from "tus-js-client";

import { captureException, captureMessage } from "@sentry/browser";

const state = {
  attempts: [],
};

const getters = {
  attempts: (state) => (project_id) => {
    return state.attempts.filter(
      (attempt) =>
        attempt.initial_project_id == project_id ||
        attempt.project_id == project_id
    );
  },
  ownAttempts: (state) => (project_id) => {
    return state.attempts.filter((attempt) => attempt.project_id == project_id);
  },
  hasRunningAttempts: (state) => {
    return state.attempts.filter(
      (attempt) => attempt.project_id && !attempt.success && !attempt.paused
    );
  },
};
const mutations = {
  addAttempt(state, attempt) {
    state.attempts.push(attempt);
  },
  attemptFailed(state, { uid, error }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.error = error;
  },
  uploadCreated(state, { uid, upload }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.upload = upload;
  },
  uploadProgress(state, { uid, progress }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.error = null;
    a.progress = progress;
  },
  uploadPaused(state, { uid, paused }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.error = null;
    a.paused = paused;
  },
  uploadFinished(state, { uid }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.error = null;
    a.success = true;
  },
  actualProjectId(state, { uid, project_id }) {
    const a = state.attempts.find((a) => a.uid === uid);
    a.project_id = project_id;
  },
};
function generateUid() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
const actions = {
  startAttempt(
    { dispatch, commit },
    { initial_project_id, user_id, file, validationEndpoint }
  ) {
    const uid = generateUid();
    commit("addAttempt", {
      uid,
      initial_project_id,
      project_id: null,
      file: {
        name: file.name,
        size: file.size,
      },
      error: null,
      upload: null,
      progress: null,
      success: false,
      paused: false,
    });

    return axios
      .post(validationEndpoint, {
        filename: file.name,
      })
      .then(({ data }) => {
        dispatch("startUpload", {
          config: data.tuspload,
          uid,
          file,
          initial_project_id,
          user_id,
        });
      })
      .catch((err) => {
        const response = err.response;
        if (!response || !response.data) {
          throw err;
        }
        commit("attemptFailed", {
          uid,
          error: {
            message: response.data.message,
            key: response.data.key,
          },
        });
      });
  },

  startUpload(
    { commit, dispatch },
    { config, uid, file, initial_project_id, user_id }
  ) {
    let gotActualProjectId = false;
    const tacho = bytesPerSecond();
    const upload = new Upload(file, {
      endpoint: config.endpoint,
      chunkSize: +config.chunkSize || Infinity,
      storeFingerprintForResuming: false,

      // add retry delays to circumvent "upload is currently locked"
      // which happens when the client has closed the connection
      // but the server did not see the close.
      // Looking at the log, the server closes the conneciont
      // 2 minutes after the client.
      retryDelays: [
        0,
        1000,
        3000,
        5000,
        // 9s
        10000,
        10000,
        10000,
        10000,
        10000,
        10000,
        // 1m9s
        10000,
        10000,
        10000,
        10000,
        10000,
        10000,
        // 2m9s
        10000,
        10000,
        10000,
        // 2m39s
      ],

      metadata: {
        uid,
        filename: file.name,
        project_id: initial_project_id,
        user_id,
        uploader_name: "webapp",
      },
      onError(error) {
        // error reporting
        let errorResponseText =
          error.originalResponse &&
          error.originalResponse._xhr &&
          error.originalResponse._xhr.responseText &&
          error.originalResponse._xhr.responseText.trim();
        const contexts = {
          file: {
            name: file.name,
            size: file.size,
            mega_size: file.size / 1000000,
            initial_project_id,
            gotActualProjectId,
            route: config.name,
          },
        };
        if (
          errorResponseText &&
          [
            "filename.already_in_the_queue",
            "upload not found",
            "A file of a different size (but the same filename) is currently being uploaded",
          ].includes(errorResponseText)
        ) {
          captureMessage(errorResponseText, { contexts });
        } else {
          captureException(error, { contexts });
        }

        const xhr =
          (error.originalResponse && error.originalResponse._xhr) || {};
        let key = (xhr.responseText || error.message).trim();
        if (key === "filename.already_in_the_queue") {
          key = "already_in_queue";
        }
        if (!key) {
          key = xhr.status;
        }
        const response = {
          status: xhr.status,
          key,
        };

        commit("attemptFailed", { uid, error: response });
      },
      onAfterResponse(req, res) {
        if (gotActualProjectId) {
          return;
        }
        const location = res.getHeader("Location");
        if (!location) {
          return;
        }
        const m = location.match(/\/(\d+)-[^/]+$/);
        if (!m || m.length < 2) {
          return;
        }
        const project_id = m[1];
        commit("actualProjectId", { uid, project_id });
        if (project_id != initial_project_id) {
          dispatch("dashboard/getPaginator", null, { root: true });
        }
        gotActualProjectId = project_id;
      },
      onProgress(bytesUploaded, bytesTotal) {
        commit("uploadProgress", {
          uid,
          progress: {
            bytesUploaded,
            bytesTotal,
            speed: tacho(bytesUploaded),
          },
        });
      },
      onSuccess() {
        commit("uploadFinished", { uid });
        if (gotActualProjectId) {
          dispatch("dashboard/getProject", gotActualProjectId, { root: true });
        }
      },
    });
    upload.start();
    commit("uploadCreated", {
      uid,
      upload: {
        route: config,
        start: () => {
          commit("uploadPaused", { uid, paused: false });
          upload.start();
        },
        pause: () => {
          commit("uploadPaused", { uid, paused: true });
          upload.abort(false);
        },
      },
    });
  },
};

function bytesPerSecond() {
  let t1 = null;
  let b1 = 0;
  let speed = null;
  return (bytes) => {
    const now = Date.now();
    if (!t1) {
      t1 = now;
      b1 = bytes;
      return null;
    }
    if (now - t1 < 1000) {
      return speed;
    }
    speed = (1000 * (bytes - b1)) / (now - t1);

    t1 = now;
    b1 = bytes;
    return speed;
  };
}
export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
